/**
 * خدمات الميزات التشغيلية والرقابية المتقدمة:
 * 1) الختم المائي التلقائي على صور الإثبات (التاريخ + الوقت + الفرع + اسم الموظف)
 * 2) شات الموظفين والمشرفين الداخلي (خاص + قنوات الفروع + تسجيلات صوتية + وسائط)
 * 3) التعليمات والبيانات الإدارية مع إثبات القراءة والاطلاع (قرأت وألتزم)
 * 4) التعليقات والملاحظات الصوتية داخل المهام
 * 5) تقارير تسليم الورديات (Shift Handover)
 * 6) مركز الملفات والوسائط الشامل (الحذف اليدوي والتلقائي الانتقائي لتوفير المساحة)
 * 7) الإغلاق التلقائي للجولات الساعية الفائتة
 * 8) تشخيص التخزين السحابي والمحلي والنسخ الاحتياطي وتصدير البيانات
 */

import type { DataGateway } from '@/data/types';
import type { Attachment, AttachmentKind, Branch, Task } from '@/domain/types';
import type { MediaHubRetentionPolicy } from '@/domain/settings';
import { newId } from '@/domain/ids';
import { deleteAttachment, uploadAttachment } from './attachment-service';
import type { Actor } from './org-service';

export const META_PREFIX = {
  chatMessage: 'tf-meta://chat-msg',
  bulletin: 'tf-meta://bulletin',
  bulletinAck: 'tf-meta://bulletin-ack',
  taskComment: 'tf-meta://task-comment',
  shiftHandover: 'tf-meta://shift-handover',
} as const;

/* ============================================================================
 * 1) الختم المائي التلقائي على صور الإثبات (Photo Watermark)
 * ========================================================================== */

export interface WatermarkOptions {
  companyName: string;
  branchName?: string | null;
  employeeName: string;
  timeZone?: string;
}

export async function applyPhotoWatermark(file: File, options: WatermarkOptions): Promise<File> {
  if (typeof document === 'undefined' || !file.type.startsWith('image/')) {
    return file;
  }
  return new Promise<File>((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        URL.revokeObjectURL(objectUrl);
        const maxDim = 2048;
        let width = img.naturalWidth || img.width || 1000;
        let height = img.naturalHeight || img.height || 750;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false });
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
        }
        if (!ctx) {
          resolve(file);
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const fontSize = Math.max(14, Math.round(width * 0.022));
        const padding = Math.max(12, Math.round(fontSize * 0.8));
        const barHeight = fontSize * 2.8 + padding * 1.4;

        const gradient = ctx.createLinearGradient(0, height - barHeight - 20, 0, height);
        gradient.addColorStop(0, 'rgba(9, 13, 22, 0)');
        gradient.addColorStop(0.35, 'rgba(9, 13, 22, 0.72)');
        gradient.addColorStop(1, 'rgba(9, 13, 22, 0.9)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, height - barHeight - 20, width, barHeight + 20);

        const nowStr = new Intl.DateTimeFormat('ar-EG', {
          dateStyle: 'medium',
          timeStyle: 'medium',
          timeZone: options.timeZone || 'Asia/Riyadh',
        }).format(new Date());

        const locationLabel = options.branchName
          ? `${options.companyName} — ${options.branchName}`
          : options.companyName;

        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'right';
        ctx.font = `bold ${fontSize}px Tahoma, "Segoe UI", sans-serif`;
        ctx.fillText(`📍 ${locationLabel}  |  👤 ${options.employeeName}`, width - padding, height - padding - fontSize * 1.25);

        ctx.fillStyle = '#93c5fd';
        ctx.font = `${Math.round(fontSize * 0.92)}px Tahoma, "Segoe UI", sans-serif`;
        ctx.fillText(`🕒 التوقيت الموثّق: ${nowStr}  •  TaskFlow Verified`, width - padding, height - padding);

        // تقنية الضغط البصري الذكي فائق الدقة (WebP مع التراجع لـ JPEG)
        const baseName = file.name.replace(/\.[^.]+$/, '');
        const exportWebP = () => {
          canvas.toBlob(
            (blob) => {
              if (blob && blob.size > 0 && blob.size < file.size) {
                const optimized = new File([blob], `${baseName}-verified.webp`, {
                  type: 'image/webp',
                  lastModified: Date.now(),
                });
                resolve(optimized);
              } else {
                exportJpeg();
              }
            },
            'image/webp',
            0.88,
          );
        };

        const exportJpeg = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const optimized = new File([blob], `${baseName}-verified.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(optimized);
            },
            'image/jpeg',
            0.85,
          );
        };

        exportWebP();
      } catch {
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });
}

/* ============================================================================
 * 2) شات الموظفين والمشرفين الداخلي (Internal Staff & Supervisor Chat)
 * ========================================================================== */

export interface ChatMessageRecord {
  id: string;
  companyId: string;
  channelId: string; // 'company:general' | 'branch:<id>' | 'dm:<u1>_<u2>'
  senderUserId: string;
  senderMembershipId: string | null;
  senderName: string;
  senderRoleLabel: string;
  text: string;
  mediaAttachmentId: string | null;
  mediaFileName: string | null;
  mediaMimeType: string | null;
  mediaKind: 'image' | 'video' | 'audio' | 'document' | null;
  createdAt: string;
}

export function buildDmChannelId(userA: string, userB: string): string {
  const sorted = [userA, userB].sort();
  return `dm:${sorted[0]}_${sorted[1]}`;
}

export function classifyMimeType(mimeType: string): 'image' | 'video' | 'audio' | 'document' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

export function attachmentKindForMime(mimeType: string): AttachmentKind {
  return mimeType.startsWith('image/') ? 'image' : 'document';
}

export async function listChatMessages(
  gateway: DataGateway,
  companyId: string,
  channelId?: string,
): Promise<ChatMessageRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });

  const messages: ChatMessageRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.chatMessage) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<ChatMessageRecord, 'id' | 'companyId' | 'createdAt'>;
      if (channelId && parsed.channelId !== channelId) continue;
      messages.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل السجلات التالفة
    }
  }
  return messages;
}

export async function sendChatMessage(
  gateway: DataGateway,
  actor: Actor,
  input: {
    channelId: string;
    senderName: string;
    senderRoleLabel: string;
    text: string;
    file?: File | null;
  },
): Promise<ChatMessageRecord> {
  let mediaAttachmentId: string | null = null;
  let mediaFileName: string | null = null;
  let mediaMimeType: string | null = null;
  let mediaKind: ChatMessageRecord['mediaKind'] = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `chat-media:${input.channelId}:${newId()}`,
    });
    mediaAttachmentId = uploaded.id;
    mediaFileName = uploaded.fileName;
    mediaMimeType = uploaded.mimeType;
    mediaKind = classifyMimeType(uploaded.mimeType);
  }

  const now = new Date().toISOString();
  const payload: Omit<ChatMessageRecord, 'id' | 'companyId' | 'createdAt'> = {
    channelId: input.channelId,
    senderUserId: actor.userId,
    senderMembershipId: actor.membershipId ?? null,
    senderName: input.senderName,
    senderRoleLabel: input.senderRoleLabel,
    text: input.text.trim(),
    mediaAttachmentId,
    mediaFileName,
    mediaMimeType,
    mediaKind,
  };

  const id = newId();
  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.chatMessage}/${input.channelId}/${id}`,
    fileName: `chat-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-chat:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteChatMessage(
  gateway: DataGateway,
  actor: Actor,
  message: ChatMessageRecord,
): Promise<void> {
  if (message.mediaAttachmentId) {
    await deleteAttachment(gateway, actor, message.mediaAttachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(message.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

export function getChatLastReadTimestamp(channelId: string, userId: string): string {
  try {
    const primary = localStorage.getItem(`tf_chat_read_${userId}_${channelId}`);
    if (primary) return primary;
    if (channelId.startsWith('dm:')) {
      const raw = channelId.slice(3);
      const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        const alt1 = `dm:${parts[1]}_${parts[0]}`;
        const alt2 = `dm:${parts[0]}:${parts[1]}`;
        const alt3 = `dm:${parts[1]}:${parts[0]}`;
        return (
          localStorage.getItem(`tf_chat_read_${userId}_${alt1}`) ||
          localStorage.getItem(`tf_chat_read_${userId}_${alt2}`) ||
          localStorage.getItem(`tf_chat_read_${userId}_${alt3}`) ||
          '1970-01-01T00:00:00.000Z'
        );
      }
    }
    return '1970-01-01T00:00:00.000Z';
  } catch {
    return '1970-01-01T00:00:00.000Z';
  }
}

export function markChatChannelRead(channelId: string, userId: string): void {
  try {
    const now = new Date().toISOString();
    localStorage.setItem(`tf_chat_read_${userId}_${channelId}`, now);
    if (channelId.startsWith('dm:')) {
      const raw = channelId.slice(3);
      const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
      if (parts.length >= 2 && parts[0] && parts[1]) {
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[1]}_${parts[0]}`, now);
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[0]}:${parts[1]}`, now);
        localStorage.setItem(`tf_chat_read_${userId}_dm:${parts[1]}:${parts[0]}`, now);
      }
    }
  } catch {
    // ignore
  }
}

export async function countUnreadChatMessages(
  gateway: DataGateway,
  companyId: string,
  userId: string,
  branchId?: string | null,
): Promise<{ total: number; dms: number; channels: number }> {
  try {
    const messages = await listChatMessages(gateway, companyId);
    let dms = 0;
    let channels = 0;
    for (const msg of messages) {
      if (msg.senderUserId === userId) continue;
      const lastRead = getChatLastReadTimestamp(msg.channelId, userId);
      if (msg.createdAt <= lastRead) continue;

      if (msg.channelId.startsWith('dm:')) {
        const raw = msg.channelId.slice(3);
        const parts = raw.includes('_') ? raw.split('_') : raw.split(':');
        if (parts.includes(userId)) {
          dms += 1;
        }
      } else if (
        msg.channelId === 'company:general' ||
        (branchId && msg.channelId === `branch:${branchId}`)
      ) {
        channels += 1;
      }
    }
    return { total: dms + channels, dms, channels };
  } catch {
    return { total: 0, dms: 0, channels: 0 };
  }
}

/* ============================================================================
 * 3) تبويبة التعليمات والبيانات الإدارية (Bulletins & Official Instructions)
 * ========================================================================== */

export type BulletinPriority = 'normal' | 'important' | 'urgent';

export interface BulletinAckRecord {
  id: string;
  bulletinId: string;
  userId: string;
  membershipId: string | null;
  userName: string;
  branchId: string | null;
  acknowledgedAt: string;
}

export interface BulletinRecord {
  id: string;
  companyId: string;
  title: string;
  body: string;
  categoryLabel: string; // 'قرار إداري' | 'تعليمات تشغيل' | 'تعميم داخلي' | 'تنبيه سلامة وجودة'
  priority: BulletinPriority;
  pinned: boolean;
  branchId: string | null; // null = كل الفروع
  branchName: string | null;
  requiresAck: boolean;
  attachmentId: string | null;
  attachmentName: string | null;
  attachmentMimeType: string | null;
  authorUserId: string;
  authorName: string;
  authorRoleLabel: string;
  createdAt: string;
  acknowledgments: BulletinAckRecord[];
}

export async function listBulletins(
  gateway: DataGateway,
  companyId: string,
): Promise<BulletinRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const acksByBulletin = new Map<string, BulletinAckRecord[]>();
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.bulletinAck) || !row.checksum) continue;
    try {
      const ack = JSON.parse(row.checksum) as Omit<BulletinAckRecord, 'id' | 'acknowledgedAt'>;
      const list = acksByBulletin.get(ack.bulletinId) ?? [];
      if (!list.some((item) => item.userId === ack.userId)) {
        list.push({
          ...ack,
          id: row.id,
          acknowledgedAt: row.createdAt,
        });
      }
      acksByBulletin.set(ack.bulletinId, list);
    } catch {
      // تجاهل
    }
  }

  const bulletins: BulletinRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(`${META_PREFIX.bulletin}/`) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<
        BulletinRecord,
        'id' | 'companyId' | 'createdAt' | 'acknowledgments'
      >;
      bulletins.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
        acknowledgments: acksByBulletin.get(row.id) ?? [],
      });
    } catch {
      // تجاهل
    }
  }

  return bulletins.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

export async function createBulletin(
  gateway: DataGateway,
  actor: Actor,
  input: {
    title: string;
    body: string;
    categoryLabel: string;
    priority: BulletinPriority;
    pinned: boolean;
    branchId: string | null;
    branchName: string | null;
    requiresAck: boolean;
    authorName: string;
    authorRoleLabel: string;
    file?: File | null;
  },
): Promise<BulletinRecord> {
  let attachmentId: string | null = null;
  let attachmentName: string | null = null;
  let attachmentMimeType: string | null = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `bulletin-media:${newId()}`,
    });
    attachmentId = uploaded.id;
    attachmentName = uploaded.fileName;
    attachmentMimeType = uploaded.mimeType;
  }

  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    title: input.title.trim(),
    body: input.body.trim(),
    categoryLabel: input.categoryLabel,
    priority: input.priority,
    pinned: input.pinned,
    branchId: input.branchId,
    branchName: input.branchName,
    requiresAck: input.requiresAck,
    attachmentId,
    attachmentName,
    attachmentMimeType,
    authorUserId: actor.userId,
    authorName: input.authorName,
    authorRoleLabel: input.authorRoleLabel,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.bulletin}/${id}`,
    fileName: `bulletin-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-bulletin:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
    acknowledgments: [],
  };
}

export async function acknowledgeBulletin(
  gateway: DataGateway,
  actor: Actor,
  input: {
    bulletinId: string;
    userName: string;
    branchId: string | null;
  },
): Promise<void> {
  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    bulletinId: input.bulletinId,
    userId: actor.userId,
    membershipId: actor.membershipId ?? null,
    userName: input.userName,
    branchId: input.branchId,
  };
  await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.bulletinAck}/${input.bulletinId}/${actor.userId}`,
    fileName: `ack-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-ack:${input.bulletinId}:${actor.userId}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });
}

export async function deleteBulletin(
  gateway: DataGateway,
  actor: Actor,
  bulletin: BulletinRecord,
): Promise<void> {
  if (bulletin.attachmentId) {
    await deleteAttachment(gateway, actor, bulletin.attachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(bulletin.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 4) التعليقات والملاحظات الصوتية داخل المهمة (Task Comments & Voice Notes)
 * ========================================================================== */

export interface TaskCommentRecord {
  id: string;
  companyId: string;
  taskId: string;
  senderUserId: string;
  senderName: string;
  senderRoleLabel: string;
  text: string;
  mediaAttachmentId: string | null;
  mediaFileName: string | null;
  mediaMimeType: string | null;
  mediaKind: 'image' | 'video' | 'audio' | 'document' | null;
  createdAt: string;
}

export async function listTaskComments(
  gateway: DataGateway,
  companyId: string,
  taskId: string,
): Promise<TaskCommentRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
  });

  const prefix = `${META_PREFIX.taskComment}/${taskId}/`;
  const comments: TaskCommentRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(prefix) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<TaskCommentRecord, 'id' | 'companyId' | 'createdAt'>;
      comments.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل
    }
  }
  return comments;
}

export async function addTaskComment(
  gateway: DataGateway,
  actor: Actor,
  input: {
    taskId: string;
    senderName: string;
    senderRoleLabel: string;
    text: string;
    file?: File | null;
  },
): Promise<TaskCommentRecord> {
  let mediaAttachmentId: string | null = null;
  let mediaFileName: string | null = null;
  let mediaMimeType: string | null = null;
  let mediaKind: TaskCommentRecord['mediaKind'] = null;

  if (input.file) {
    const uploaded = await uploadAttachment(gateway, actor, {
      companyId: actor.companyId,
      taskId: null,
      file: input.file,
      kind: attachmentKindForMime(input.file.type),
      clientRef: `comment-media:${input.taskId}:${newId()}`,
    });
    mediaAttachmentId = uploaded.id;
    mediaFileName = uploaded.fileName;
    mediaMimeType = uploaded.mimeType;
    mediaKind = classifyMimeType(uploaded.mimeType);
  }

  const id = newId();
  const now = new Date().toISOString();
  const payload: Omit<TaskCommentRecord, 'id' | 'companyId' | 'createdAt'> = {
    taskId: input.taskId,
    senderUserId: actor.userId,
    senderName: input.senderName,
    senderRoleLabel: input.senderRoleLabel,
    text: input.text.trim(),
    mediaAttachmentId,
    mediaFileName,
    mediaMimeType,
    mediaKind,
  };

  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.taskComment}/${input.taskId}/${id}`,
    fileName: `comment-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-comment:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteTaskComment(
  gateway: DataGateway,
  actor: Actor,
  comment: TaskCommentRecord,
): Promise<void> {
  if (comment.mediaAttachmentId) {
    await deleteAttachment(gateway, actor, comment.mediaAttachmentId).catch(() => undefined);
  }
  const now = new Date().toISOString();
  await gateway.table('attachments').update(comment.id, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 5) تقارير تسليم الوردية (Shift Handover Reports)
 * ========================================================================== */

export interface ShiftHandoverRecord {
  id: string;
  companyId: string;
  branchId: string | null;
  branchName: string;
  shiftLabel: string;
  completedCount: number;
  pendingCount: number;
  overdueCount: number;
  summaryNotes: string;
  urgentIssues: string;
  nextShiftNotes: string;
  authorUserId: string;
  authorName: string;
  createdAt: string;
}

export async function listShiftHandovers(
  gateway: DataGateway,
  companyId: string,
): Promise<ShiftHandoverRecord[]> {
  const rows = await gateway.table('attachments').list({
    filter: {
      companyId: { op: 'eq', value: companyId },
      deletedAt: { op: 'isNull' },
    },
    orderBy: [{ field: 'createdAt', direction: 'desc' }],
  });

  const handovers: ShiftHandoverRecord[] = [];
  for (const row of rows) {
    if (!row.storagePath.startsWith(META_PREFIX.shiftHandover) || !row.checksum) continue;
    try {
      const parsed = JSON.parse(row.checksum) as Omit<ShiftHandoverRecord, 'id' | 'companyId' | 'createdAt'>;
      handovers.push({
        ...parsed,
        id: row.id,
        companyId: row.companyId,
        createdAt: row.createdAt,
      });
    } catch {
      // تجاهل
    }
  }
  return handovers;
}

export async function createShiftHandover(
  gateway: DataGateway,
  actor: Actor,
  input: Omit<ShiftHandoverRecord, 'id' | 'companyId' | 'createdAt' | 'authorUserId'>,
): Promise<ShiftHandoverRecord> {
  const id = newId();
  const now = new Date().toISOString();
  const payload = {
    ...input,
    authorUserId: actor.userId,
  };
  const row = await gateway.table('attachments').insert({
    id,
    companyId: actor.companyId,
    taskId: null,
    taskAssigneeId: null,
    storagePath: `${META_PREFIX.shiftHandover}/${id}`,
    fileName: `handover-${id}.json`,
    mimeType: 'application/json',
    sizeBytes: 0,
    kind: 'note',
    uploadState: 'pending',
    uploadedByUserId: actor.userId,
    checksum: JSON.stringify(payload),
    clientRef: `tf-handover:${id}`,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  });

  return {
    ...payload,
    id: row.id,
    companyId: row.companyId,
    createdAt: row.createdAt,
  };
}

export async function deleteShiftHandover(
  gateway: DataGateway,
  handoverId: string,
): Promise<void> {
  const now = new Date().toISOString();
  await gateway.table('attachments').update(handoverId, {
    deletedAt: now,
    uploadState: 'deleted',
    updatedAt: now,
  });
}

/* ============================================================================
 * 6) قسم الملفات والوسائط الشامل (Media & Files Hub + Selective Auto-Retention)
 * ========================================================================== */

export type MediaSourceCategory =
  | 'task_proof'
  | 'chat_media'
  | 'bulletin_media'
  | 'manual_media'
  | 'branding';

export type MediaSubSource =
  | 'task_proof'
  | 'task_comment_media'
  | 'chat_general'
  | 'chat_branch'
  | 'chat_direct'
  | 'bulletin_media'
  | 'manual_media'
  | 'branding';
export type MediaFileType = 'image' | 'video' | 'audio' | 'document';

export function isVoiceRecordingFile(fileName: string): boolean {
  const lower = (fileName || '').toLowerCase();
  return (
    lower.startsWith('voice-note-') ||
    lower.startsWith('voice-comment-') ||
    lower.startsWith('voice-')
  );
}

/**
 * فحص صارم ومحكم لجميع أشكال الهوية والبراند:
 * الصور الشخصية (Avatars)، شعار المنشأة، شعار وهوية البراند، صور الفروع، وهوية المنصة.
 * هذه الملفات تتمتع بحصانة مطلقة ودائمة من أي حذف تلقائي أو دوري.
 */
export function isBrandingAttachment(attachment: Attachment): boolean {
  if (attachment.kind === 'logo' || attachment.kind === 'avatar') return true;
  const ref = (attachment.clientRef ?? '').toLowerCase();
  const path = (attachment.storagePath ?? '').toLowerCase();
  const name = (attachment.fileName ?? '').toLowerCase();

  if (
    ref.includes('logo') ||
    ref.includes('avatar') ||
    ref.includes('branding') ||
    ref.includes('company-logo') ||
    ref.includes('branch-logo') ||
    ref.includes('brand-logo') ||
    ref.includes('profile-')
  ) {
    return true;
  }
  if (
    path.includes('/logos/') ||
    path.includes('/avatars/') ||
    path.includes('/branding/') ||
    path.includes('company_logos/') ||
    path.includes('branch_logos/')
  ) {
    return true;
  }
  if (name.includes('logo') || name.includes('avatar') || name.includes('favicon')) {
    return true;
  }
  return false;
}

/**
 * فحص ملفات ومستندات وجداول الدليل التشغيلي للمتجر (Store Manual).
 */
export function isStoreManualAttachment(attachment: Attachment): boolean {
  const ref = (attachment.clientRef ?? '').toLowerCase();
  const path = (attachment.storagePath ?? '').toLowerCase();

  if (
    ref.startsWith('manual-doc:') ||
    ref.startsWith('tf-manual:') ||
    ref.startsWith('tf-staffrole:') ||
    ref.startsWith('tf-sheet:') ||
    ref.includes('manual') ||
    ref.includes('staff-role')
  ) {
    return true;
  }
  if (
    path.startsWith('tf-meta://manual-item') ||
    path.startsWith('tf-meta://staff-role') ||
    path.startsWith('tf-meta://manual-sheet') ||
    path.includes('/manual-') ||
    path.includes('manual-doc')
  ) {
    return true;
  }
  return false;
}

export interface MediaHubItem {
  attachment: Attachment;
  sourceCategory: MediaSourceCategory;
  subSource: MediaSubSource;
  sourceLabel: string;
  mediaType: MediaFileType;
  isVoiceNote: boolean;
  mediaTypeLabel: string;
  taskTitle: string | null;
  taskStatus: Task['status'] | null;
  branchId: string | null;
  branchName: string | null;
  uploaderName: string;
}

export function resolveMediaSource(attachment: Attachment): {
  category: MediaSourceCategory;
  subSource: MediaSubSource;
  label: string;
  extractedBranchId: string | null;
  extractedTaskId: string | null;
} {
  // 1) شعارات وهوية المنشأة، البراند، الفروع، والصور الشخصية (حصانة تامة)
  if (isBrandingAttachment(attachment)) {
    return {
      category: 'branding',
      subSource: 'branding',
      label: 'شعارات وهوية المنشأة والفروع والملف الشخصي',
      extractedBranchId: null,
      extractedTaskId: null,
    };
  }

  // 2) ملفات ومستندات الدليل التشغيلي للمتجر
  if (isStoreManualAttachment(attachment)) {
    return {
      category: 'manual_media',
      subSource: 'manual_media',
      label: 'ملفات ومستندات الدليل التشغيلي',
      extractedBranchId: null,
      extractedTaskId: null,
    };
  }

  const ref = attachment.clientRef ?? '';
  if (ref.startsWith('comment-media:')) {
    const parts = ref.split(':');
    return {
      category: 'chat_media',
      subSource: 'task_comment_media',
      label: 'مرفقات تعليقات المهام',
      extractedBranchId: null,
      extractedTaskId: parts[1] || null,
    };
  }
  if (ref.startsWith('chat-media:')) {
    if (ref.startsWith('chat-media:company:general')) {
      return {
        category: 'chat_media',
        subSource: 'chat_general',
        label: 'شات القناة العامة',
        extractedBranchId: null,
        extractedTaskId: null,
      };
    }
    if (ref.startsWith('chat-media:branch:')) {
      const parts = ref.split(':');
      return {
        category: 'chat_media',
        subSource: 'chat_branch',
        label: 'شات الفروع',
        extractedBranchId: parts[2] || null,
        extractedTaskId: null,
      };
    }
    if (ref.startsWith('chat-media:dm:')) {
      return {
        category: 'chat_media',
        subSource: 'chat_direct',
        label: 'المحادثات الخاصة (DM)',
        extractedBranchId: null,
        extractedTaskId: null,
      };
    }
    return {
      category: 'chat_media',
      subSource: 'chat_general',
      label: 'وسائط الشات والمحادثات',
      extractedBranchId: null,
      extractedTaskId: null,
    };
  }
  if (ref.startsWith('bulletin-media:')) {
    return {
      category: 'bulletin_media',
      subSource: 'bulletin_media',
      label: 'مرفقات التعليمات والبيانات',
      extractedBranchId: null,
      extractedTaskId: null,
    };
  }
  return {
    category: 'task_proof',
    subSource: 'task_proof',
    label: 'إثباتات ومرفقات المهام',
    extractedBranchId: null,
    extractedTaskId: attachment.taskId ?? null,
  };
}

export function resolveMediaTypeLabel(type: MediaFileType, isVoiceNote = false): string {
  switch (type) {
    case 'image':
      return 'صورة';
    case 'video':
      return 'فيديو';
    case 'audio':
      return isVoiceNote ? 'تسجيل صوتي مباشر' : 'مقطع صوتي مرفق';
    case 'document':
      return 'مستند / ملف';
  }
}

export async function listMediaHubItems(
  gateway: DataGateway,
  companyId: string,
  options?: { includeBranding?: boolean },
): Promise<MediaHubItem[]> {
  const [attachments, tasks, branches, profiles] = await Promise.all([
    gateway.table('attachments').list({
      filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    }),
    gateway.table('tasks').list({
      filter: { companyId: { op: 'eq', value: companyId } },
    }).catch(() => [] as Task[]),
    gateway.table('branches').list({
      filter: { companyId: { op: 'eq', value: companyId } },
    }).catch(() => [] as Branch[]),
    gateway.commands.listCompanyProfiles({ companyId }).catch(() => []),
  ]);

  const tasksById = new Map(tasks.map((t) => [t.id, t]));
  const branchesById = new Map(branches.map((b) => [b.id, b.name]));
  const namesByUserId = new Map(profiles.map((p) => [p.userId, p.fullName]));

  const items: MediaHubItem[] = [];
  for (const att of attachments) {
    if (att.uploadState !== 'uploaded' || att.deletedAt || att.storagePath.startsWith('tf-meta://')) {
      continue;
    }
    const source = resolveMediaSource(att);
    if (!options?.includeBranding && source.category === 'branding') {
      continue;
    }
    const mediaType = classifyMimeType(att.mimeType);
    const isVoiceNote = mediaType === 'audio' && isVoiceRecordingFile(att.fileName);
    const targetTaskId = att.taskId ?? source.extractedTaskId;
    const parentTask = targetTaskId ? tasksById.get(targetTaskId) ?? null : null;
    const branchId = parentTask?.branchId ?? source.extractedBranchId ?? null;
    const branchName = branchId ? branchesById.get(branchId) ?? null : null;

    items.push({
      attachment: att,
      sourceCategory: source.category,
      subSource: source.subSource,
      sourceLabel: source.label,
      mediaType,
      isVoiceNote,
      mediaTypeLabel: resolveMediaTypeLabel(mediaType, isVoiceNote),
      taskTitle: parentTask?.title ?? null,
      taskStatus: parentTask?.status ?? null,
      branchId,
      branchName,
      uploaderName: namesByUserId.get(att.uploadedByUserId) ?? 'عضو بالمنشأة',
    });
  }

  return items;
}

export function isItemEligibleForSelectiveRetention(
  item: MediaHubItem,
  policy: MediaHubRetentionPolicy,
  now: Date = new Date(),
): boolean {
  // 1) حصانة مطلقة وغير قابلة للمساس: الصور الشخصية للملف الشخصي، وشعار المنشأة، وهوية البراند، وصور الفروع، وهوية المنصة مستثناة نهائياً من أي حذف
  if (item.sourceCategory === 'branding' || isBrandingAttachment(item.attachment)) {
    return false;
  }

  // 2) تصفية الفرع المحدد
  if (policy.targetBranchId && policy.targetBranchId !== 'all') {
    if (item.branchId !== policy.targetBranchId) return false;
  }

  // 3) تصفية الحد الأدنى لحجم الملف
  const minBytes = Math.max(0, Number(policy.minFileSizeKb) || 0) * 1024;
  if (minBytes > 0 && (item.attachment.sizeBytes || 0) < minBytes) {
    return false;
  }

  // 4) تصفية العمر بالأيام (0 = فوري بدون قيد عمر)
  const days = Math.max(0, Number(policy.autoDeleteAfterDays) || 0);
  if (days > 0) {
    const ageMs = now.getTime() - new Date(item.attachment.createdAt).getTime();
    if (ageMs < days * 86_400_000) return false;
  }

  // 5) ملفات ومستندات الدليل التشغيلي للمتجر (Store Manual) - حرية تامة في الاختيار أو الاستثناء
  if (item.sourceCategory === 'manual_media') {
    if (!policy.deleteStoreManualMedia) return false;
    if (item.mediaType === 'image' && !(policy.manualDeleteImages ?? policy.deleteImages)) return false;
    if (item.mediaType === 'document' && !(policy.manualDeleteDocuments ?? policy.deleteDocuments)) return false;
    return true;
  }

  // 6) إثباتات ومرفقات المهام
  if (item.sourceCategory === 'task_proof' || item.subSource === 'task_comment_media') {
    if (!policy.deleteTaskProofMedia) return false;
    if (policy.completedTasksOnly && item.taskStatus !== 'completed') return false;

    if (item.mediaType === 'image' && !(policy.taskDeleteImages ?? policy.deleteImages)) return false;
    if (item.mediaType === 'video' && !(policy.taskDeleteVideos ?? policy.deleteVideos)) return false;
    if (item.mediaType === 'document' && !(policy.taskDeleteDocuments ?? policy.deleteDocuments)) return false;
    if (item.mediaType === 'audio') {
      if (item.isVoiceNote) {
        if (!(policy.taskDeleteVoiceNotes ?? policy.deleteAudio)) return false;
      } else {
        if (!(policy.taskDeleteAudioClips ?? policy.deleteAudio)) return false;
      }
    }
    return true;
  }

  // 7) وسائط ورسائل الشات
  if (item.sourceCategory === 'chat_media') {
    if (!policy.deleteChatMedia) return false;

    // فحص نطاق المحادثة
    if (item.subSource === 'chat_general' && policy.chatScopeGeneral === false) return false;
    if (item.subSource === 'chat_branch' && policy.chatScopeBranches === false) return false;
    if (item.subSource === 'chat_direct' && policy.chatScopeDirect === false) return false;

    // فحص نوع وسائط الشات بالتفصيل
    if (item.mediaType === 'image' && !(policy.chatDeleteImages ?? policy.deleteImages)) return false;
    if (item.mediaType === 'video' && !(policy.chatDeleteVideos ?? policy.deleteVideos)) return false;
    if (item.mediaType === 'document' && !(policy.chatDeleteDocuments ?? policy.deleteDocuments)) return false;
    if (item.mediaType === 'audio') {
      if (item.isVoiceNote) {
        if (!(policy.chatDeleteVoiceNotes ?? policy.deleteAudio)) return false;
      } else {
        if (!(policy.chatDeleteAudioClips ?? policy.deleteAudio)) return false;
      }
    }
    return true;
  }

  // 8) مرفقات التعليمات والتعميمات الإدارية (حرية كاملة في الاختيار)
  if (item.sourceCategory === 'bulletin_media') {
    if (!policy.deleteBulletinMedia) return false;
    if (item.mediaType === 'image' && !(policy.bulletinDeleteImages ?? policy.deleteImages)) return false;
    if (item.mediaType === 'video' && !(policy.bulletinDeleteVideos ?? policy.deleteVideos)) return false;
    if (item.mediaType === 'document' && !(policy.bulletinDeleteDocuments ?? policy.deleteDocuments)) return false;
    if (item.mediaType === 'audio' && !(policy.bulletinDeleteAudio ?? policy.deleteAudio)) return false;
    return true;
  }

  return true;
}

export interface SelectiveRetentionBreakdown {
  chatImages: number;
  chatVideos: number;
  chatDocuments: number;
  chatAudioClips: number;
  chatVoiceNotes: number;
  chatTextMessages: number;
  taskMedia: number;
  taskComments: number;
  bulletinMedia: number;
  bulletinRecords: number;
  handoverRecords: number;
  manualMedia: number;
  brandingMedia: number;
}

export interface SelectiveRetentionResult {
  filesCount: number;
  recordsCount: number;
  totalRemovedCount: number;
  bytesFreed: number;
  deletedIds: string[];
  breakdown: SelectiveRetentionBreakdown;
}

export async function runSelectiveMediaRetention(
  gateway: DataGateway,
  actor: Actor,
  policy: MediaHubRetentionPolicy,
  options: { dryRun: boolean },
): Promise<SelectiveRetentionResult> {
  const now = new Date();
  const days = Math.max(0, Number(policy.autoDeleteAfterDays) || 0);
  const isAgeEligible = (createdAt: string): boolean => {
    if (days <= 0) return true;
    return now.getTime() - new Date(createdAt).getTime() >= days * 86_400_000;
  };

  const allItems = await listMediaHubItems(gateway, actor.companyId, {
    includeBranding: false, // الهوية والبراند والصور الشخصية لا تدرج نهائياً في عمليات الحذف
  });
  const eligibleFiles = allItems.filter((item) => isItemEligibleForSelectiveRetention(item, policy, now));
  const eligibleFileIds = new Set(eligibleFiles.map((item) => item.attachment.id));

  const breakdown: SelectiveRetentionBreakdown = {
    chatImages: 0,
    chatVideos: 0,
    chatDocuments: 0,
    chatAudioClips: 0,
    chatVoiceNotes: 0,
    chatTextMessages: 0,
    taskMedia: 0,
    taskComments: 0,
    bulletinMedia: 0,
    bulletinRecords: 0,
    handoverRecords: 0,
    manualMedia: 0,
    brandingMedia: 0,
  };

  for (const item of eligibleFiles) {
    if (item.sourceCategory === 'chat_media' && item.subSource !== 'task_comment_media') {
      if (item.mediaType === 'image') breakdown.chatImages += 1;
      else if (item.mediaType === 'video') breakdown.chatVideos += 1;
      else if (item.mediaType === 'document') breakdown.chatDocuments += 1;
      else if (item.mediaType === 'audio') {
        if (item.isVoiceNote) breakdown.chatVoiceNotes += 1;
        else breakdown.chatAudioClips += 1;
      }
    } else if (item.sourceCategory === 'task_proof' || item.subSource === 'task_comment_media') {
      breakdown.taskMedia += 1;
    } else if (item.sourceCategory === 'bulletin_media') {
      breakdown.bulletinMedia += 1;
    } else if (item.sourceCategory === 'manual_media') {
      breakdown.manualMedia += 1;
    } else if (item.sourceCategory === 'branding') {
      breakdown.brandingMedia += 1;
    }
  }

  // فحص السجلات النصية والبيانات الوصفية (الرسائل النصية، تعليقات المهام، التعليمات، تقارير الوردية)
  const wantChatText = Boolean(policy.deleteChatMedia && policy.chatDeleteTextMessages);
  const wantTaskComments = Boolean(policy.deleteTaskProofMedia && policy.taskDeleteComments);
  const wantBulletins = Boolean(policy.deleteBulletinRecords);
  const wantHandovers = Boolean(policy.deleteHandoverRecords);

  const eligibleMetaRowIds: string[] = [];
  const orphanWrapperRowIds: string[] = [];

  if (wantChatText || wantTaskComments || wantBulletins || wantHandovers || eligibleFileIds.size > 0) {
    const [allAttachments, tasks] = await Promise.all([
      gateway.table('attachments').list({
        filter: { companyId: { op: 'eq', value: actor.companyId }, deletedAt: { op: 'isNull' } },
      }).catch(() => [] as Attachment[]),
      wantTaskComments
        ? gateway.table('tasks').list({
            filter: { companyId: { op: 'eq', value: actor.companyId } },
          }).catch(() => [] as Task[])
        : Promise.resolve([] as Task[]),
    ]);
    const tasksById = new Map(tasks.map((t) => [t.id, t]));

    for (const row of allAttachments) {
      if (row.deletedAt || !row.storagePath.startsWith('tf-meta://') || !row.checksum) continue;
      if (!isAgeEligible(row.createdAt)) continue;

      // أ) رسائل الشات النصية
      if (row.storagePath.startsWith(META_PREFIX.chatMessage)) {
        try {
          const parsed = JSON.parse(row.checksum) as {
            channelId?: string;
            text?: string;
            mediaAttachmentId?: string | null;
          };
          const ch = parsed.channelId ?? 'company:general';
          let scopeOk = true;
          if (ch === 'company:general' && policy.chatScopeGeneral === false) scopeOk = false;
          if (ch.startsWith('branch:') && policy.chatScopeBranches === false) scopeOk = false;
          if (ch.startsWith('dm:') && policy.chatScopeDirect === false) scopeOk = false;
          if (policy.targetBranchId && policy.targetBranchId !== 'all') {
            if (ch !== `branch:${policy.targetBranchId}`) scopeOk = false;
          }

          if (scopeOk && wantChatText) {
            const hasRemainingMedia =
              parsed.mediaAttachmentId && !eligibleFileIds.has(parsed.mediaAttachmentId);
            if (!hasRemainingMedia) {
              eligibleMetaRowIds.push(row.id);
              breakdown.chatTextMessages += 1;
              continue;
            }
          }

          // تنظيف فقاعة الرسالة الفارغة إذا تم حذف مرفقها الوحيد ولم يكن بها نص
          if (
            parsed.mediaAttachmentId &&
            eligibleFileIds.has(parsed.mediaAttachmentId) &&
            !(parsed.text ?? '').trim()
          ) {
            orphanWrapperRowIds.push(row.id);
          }
        } catch {
          // تجاهل
        }
        continue;
      }

      // ب) تعليقات المهام
      if (wantTaskComments && row.storagePath.startsWith(META_PREFIX.taskComment)) {
        try {
          const parsed = JSON.parse(row.checksum) as { taskId?: string };
          const parentTask = parsed.taskId ? tasksById.get(parsed.taskId) ?? null : null;
          if (policy.completedTasksOnly && parentTask?.status !== 'completed') continue;
          if (
            policy.targetBranchId &&
            policy.targetBranchId !== 'all' &&
            parentTask?.branchId !== policy.targetBranchId
          ) {
            continue;
          }
          eligibleMetaRowIds.push(row.id);
          breakdown.taskComments += 1;
        } catch {
          // تجاهل
        }
        continue;
      }

      // ج) سجلات التعليمات الإدارية
      if (
        wantBulletins &&
        row.storagePath.startsWith(`${META_PREFIX.bulletin}/`) &&
        !row.storagePath.startsWith(META_PREFIX.bulletinAck)
      ) {
        try {
          const parsed = JSON.parse(row.checksum) as { branchId?: string | null };
          if (
            policy.targetBranchId &&
            policy.targetBranchId !== 'all' &&
            parsed.branchId !== policy.targetBranchId
          ) {
            continue;
          }
          eligibleMetaRowIds.push(row.id);
          breakdown.bulletinRecords += 1;
        } catch {
          // تجاهل
        }
        continue;
      }

      // د) تقارير تسليم الورديات
      if (wantHandovers && row.storagePath.startsWith(META_PREFIX.shiftHandover)) {
        try {
          const parsed = JSON.parse(row.checksum) as { branchId?: string | null };
          if (
            policy.targetBranchId &&
            policy.targetBranchId !== 'all' &&
            parsed.branchId !== policy.targetBranchId
          ) {
            continue;
          }
          eligibleMetaRowIds.push(row.id);
          breakdown.handoverRecords += 1;
        } catch {
          // تجاهل
        }
        continue;
      }
    }
  }

  const totalPreviewBytes = eligibleFiles.reduce((sum, item) => sum + (item.attachment.sizeBytes || 0), 0);

  if (options.dryRun || (eligibleFiles.length === 0 && eligibleMetaRowIds.length === 0)) {
    return {
      filesCount: eligibleFiles.length,
      recordsCount: eligibleMetaRowIds.length,
      totalRemovedCount: eligibleFiles.length + eligibleMetaRowIds.length,
      bytesFreed: totalPreviewBytes,
      deletedIds: [...eligibleFiles.map((item) => item.attachment.id), ...eligibleMetaRowIds],
      breakdown,
    };
  }

  let filesCount = 0;
  let recordsCount = 0;
  let bytesFreed = 0;
  const deletedIds: string[] = [];

  for (const item of eligibleFiles) {
    try {
      await deleteAttachment(gateway, actor, item.attachment.id);
      filesCount += 1;
      bytesFreed += item.attachment.sizeBytes || 0;
      deletedIds.push(item.attachment.id);
    } catch {
      // نستمر في حذف بقية الملفات المؤهلة
    }
  }

  const nowIso = new Date().toISOString();
  for (const metaId of [...eligibleMetaRowIds, ...orphanWrapperRowIds]) {
    try {
      await gateway.table('attachments').update(metaId, {
        deletedAt: nowIso,
        uploadState: 'deleted',
        updatedAt: nowIso,
      });
      if (!orphanWrapperRowIds.includes(metaId)) {
        recordsCount += 1;
        deletedIds.push(metaId);
      }
    } catch {
      // تجاهل
    }
  }

  return {
    filesCount,
    recordsCount,
    totalRemovedCount: filesCount + recordsCount,
    bytesFreed,
    deletedIds,
    breakdown,
  };
}

/* ============================================================================
 * 7) الإغلاق التلقائي للجولات الساعية الفائتة (Auto-Expire Missed Intraday Rounds)
 * ========================================================================== */

export async function autoExpireMissedIntradayRounds(
  gateway: DataGateway,
  actor: Actor,
  tasks: readonly Task[],
): Promise<number> {
  if (!actor.membershipId) return 0;
  const nowMs = Date.now();
  let expiredCount = 0;

  for (const task of tasks) {
    if (
      task.deletedAt ||
      (task.status !== 'pending' && task.status !== 'scheduled' && task.status !== 'in_progress') ||
      !task.dueAt
    ) {
      continue;
    }
    const isRecurringRound =
      task.occurrenceKey.includes('::slot:') ||
      Boolean(task.templateId) ||
      task.title.includes('جولة') ||
      task.title.includes('كل ساعة');

    if (!isRecurringRound) {
      continue;
    }

    const dueMs = new Date(task.dueAt).getTime();
    // إذا انتهى وقت الجولة الساعية ومضى عليه أكثر من 5 دقائق سماح، تُغلق الجولة الفائتة تلقائيًا
    if (!Number.isNaN(dueMs) && nowMs > dueMs + 5 * 60_000) {
      try {
        await gateway.commands.cancelTask({
          taskId: task.id,
          membershipId: actor.membershipId,
          actorUserId: actor.userId,
          reason: 'إغلاق تلقائي لجولة ساعية فائتة بعد دخول وقت الجولة التالية',
        });
        expiredCount += 1;
      } catch {
        // قد لا يملك المستخدم الحالي صلاحية إلغاء هذه المهمة؛ نتجاوزها بصمت
      }
    }
  }
  return expiredCount;
}

/* ============================================================================
 * 8) تشخيص التخزين السحابي والمحلي والنسخ الاحتياطي والتصدير
 * ========================================================================== */

export interface StorageDiagnostics {
  gatewayKind: 'local' | 'supabase';
  cloudFilesCount: number;
  cloudTotalBytes: number;
  bySource: Record<MediaSourceCategory, { count: number; bytes: number }>;
  byType: Record<MediaFileType, { count: number; bytes: number }>;
  chatBreakdown: {
    images: { count: number; bytes: number };
    videos: { count: number; bytes: number };
    documents: { count: number; bytes: number };
    audioClips: { count: number; bytes: number };
    voiceNotes: { count: number; bytes: number };
    textMessages: number;
    generalChannelMsgs: number;
    branchChannelMsgs: number;
    directMsgs: number;
  };
  counts: {
    tasks: number;
    completedTasks: number;
    templates: number;
    branches: number;
    members: number;
    chatMessages: number;
    taskComments: number;
    bulletins: number;
    handovers: number;
    manualItems: number;
  };
  localQuotaBytes: number | null;
  localUsageBytes: number | null;
  localStorageBytes: number;
}

export async function getStorageDiagnostics(
  gateway: DataGateway,
  companyId: string,
): Promise<StorageDiagnostics> {
  const [items, tasks, templates, branches, memberships, chatMsgs, bulletins, handovers, allAtts] =
    await Promise.all([
      listMediaHubItems(gateway, companyId, { includeBranding: true }),
      gateway.table('tasks').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => [] as Task[]),
      gateway.table('task_templates').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      gateway.table('memberships').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => []),
      listChatMessages(gateway, companyId).catch(() => []),
      listBulletins(gateway, companyId).catch(() => []),
      listShiftHandovers(gateway, companyId).catch(() => []),
      gateway.table('attachments').list({ filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } } }).catch(() => [] as Attachment[]),
    ]);

  const taskCommentsCount = allAtts.filter((a) =>
    a.storagePath.startsWith(META_PREFIX.taskComment),
  ).length;

  const manualItemsCount = allAtts.filter(
    (a) =>
      a.storagePath.startsWith('tf-meta://manual-item') ||
      a.storagePath.startsWith('tf-meta://manual-sheet') ||
      a.storagePath.startsWith('tf-meta://staff-role') ||
      a.clientRef?.startsWith('tf-manual:') ||
      a.clientRef?.startsWith('manual-doc:'),
  ).length;

  const bySource: StorageDiagnostics['bySource'] = {
    task_proof: { count: 0, bytes: 0 },
    chat_media: { count: 0, bytes: 0 },
    bulletin_media: { count: 0, bytes: 0 },
    manual_media: { count: 0, bytes: 0 },
    branding: { count: 0, bytes: 0 },
  };
  const byType: StorageDiagnostics['byType'] = {
    image: { count: 0, bytes: 0 },
    video: { count: 0, bytes: 0 },
    audio: { count: 0, bytes: 0 },
    document: { count: 0, bytes: 0 },
  };
  const chatBreakdown: StorageDiagnostics['chatBreakdown'] = {
    images: { count: 0, bytes: 0 },
    videos: { count: 0, bytes: 0 },
    documents: { count: 0, bytes: 0 },
    audioClips: { count: 0, bytes: 0 },
    voiceNotes: { count: 0, bytes: 0 },
    textMessages: chatMsgs.length,
    generalChannelMsgs: chatMsgs.filter((m) => m.channelId === 'company:general').length,
    branchChannelMsgs: chatMsgs.filter((m) => m.channelId.startsWith('branch:')).length,
    directMsgs: chatMsgs.filter((m) => m.channelId.startsWith('dm:')).length,
  };

  let cloudTotalBytes = 0;
  for (const item of items) {
    const b = item.attachment.sizeBytes || 0;
    cloudTotalBytes += b;
    bySource[item.sourceCategory].count += 1;
    bySource[item.sourceCategory].bytes += b;
    byType[item.mediaType].count += 1;
    byType[item.mediaType].bytes += b;

    if (item.sourceCategory === 'chat_media' && item.subSource !== 'task_comment_media') {
      if (item.mediaType === 'image') {
        chatBreakdown.images.count += 1;
        chatBreakdown.images.bytes += b;
      } else if (item.mediaType === 'video') {
        chatBreakdown.videos.count += 1;
        chatBreakdown.videos.bytes += b;
      } else if (item.mediaType === 'document') {
        chatBreakdown.documents.count += 1;
        chatBreakdown.documents.bytes += b;
      } else if (item.mediaType === 'audio') {
        if (item.isVoiceNote) {
          chatBreakdown.voiceNotes.count += 1;
          chatBreakdown.voiceNotes.bytes += b;
        } else {
          chatBreakdown.audioClips.count += 1;
          chatBreakdown.audioClips.bytes += b;
        }
      }
    }
  }

  let localQuotaBytes: number | null = null;
  let localUsageBytes: number | null = null;
  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      const est = await navigator.storage.estimate();
      localQuotaBytes = est.quota ?? null;
      localUsageBytes = est.usage ?? null;
    } catch {
      // تجاهل
    }
  }

  let localStorageBytes = 0;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i) ?? '';
      const v = localStorage.getItem(k) ?? '';
      localStorageBytes += (k.length + v.length) * 2;
    }
  } catch {
    // تجاهل
  }

  return {
    gatewayKind: gateway.kind,
    cloudFilesCount: items.length,
    cloudTotalBytes,
    bySource,
    byType,
    chatBreakdown,
    counts: {
      tasks: tasks.length,
      completedTasks: tasks.filter((t) => t.status === 'completed').length,
      templates: templates.length,
      branches: branches.length,
      members: memberships.length,
      chatMessages: chatMsgs.length,
      taskComments: taskCommentsCount,
      bulletins: bulletins.length,
      handovers: handovers.length,
      manualItems: manualItemsCount,
    },
    localQuotaBytes,
    localUsageBytes,
    localStorageBytes,
  };
}

export async function exportCompanyBackupJson(
  gateway: DataGateway,
  companyId: string,
  companyName: string,
): Promise<void> {
  const [
    company,
    brands,
    branches,
    categories,
    memberships,
    roles,
    templates,
    tasks,
    settings,
    bulletins,
    handovers,
    chatMessages,
  ] = await Promise.all([
    gateway.table('companies').get(companyId).catch(() => null),
    gateway.table('brands').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_categories').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('memberships').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('roles').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_templates').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('tasks').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('settings').list({ filter: { scopeId: { op: 'eq', value: companyId } } }).catch(() => []),
    listBulletins(gateway, companyId).catch(() => []),
    listShiftHandovers(gateway, companyId).catch(() => []),
    listChatMessages(gateway, companyId).catch(() => []),
  ]);

  const backupPayload = {
    schemaVersion: '2.0',
    exportedAt: new Date().toISOString(),
    companyName,
    companyId,
    data: {
      company,
      brands,
      branches,
      categories,
      memberships,
      roles,
      templates,
      tasks,
      settings,
      bulletins,
      shiftHandovers: handovers,
      chatMessages,
    },
  };

  const blob = new Blob([JSON.stringify(backupPayload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const datePart = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `taskflow-backup-${company?.slug ?? 'company'}-${datePart}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportTasksCsv(
  gateway: DataGateway,
  companyId: string,
  companySlug = 'company',
): Promise<void> {
  const [tasks, branches, categories] = await Promise.all([
    gateway.table('tasks').list({
      filter: { companyId: { op: 'eq', value: companyId }, deletedAt: { op: 'isNull' } },
      orderBy: [{ field: 'createdAt', direction: 'desc' }],
    }),
    gateway.table('branches').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
    gateway.table('task_categories').list({ filter: { companyId: { op: 'eq', value: companyId } } }).catch(() => []),
  ]);

  const branchMap = new Map(branches.map((b) => [b.id, b.name]));
  const categoryMap = new Map(categories.map((c) => [c.id, c.name]));

  const headers = [
    'معرف المهمة',
    'العنوان',
    'الفرع',
    'الفئة',
    'الحالة',
    'حالة الاعتماد',
    'الأولوية',
    'التاريخ المحلي',
    'وقت البدء',
    'الموعد النهائي',
    'تاريخ الإنجاز',
  ];

  const escapeCsv = (val: string | null | undefined) => {
    const str = String(val ?? '').replace(/"/g, '""');
    return `"${str}"`;
  };

  const lines = [
    headers.map(escapeCsv).join(','),
    ...tasks.map((t) =>
      [
        t.id,
        t.title,
        t.branchId ? branchMap.get(t.branchId) ?? t.branchId : 'عام',
        categoryMap.get(t.categoryId) ?? t.categoryId,
        t.status,
        t.approvalState,
        t.priority,
        t.localDate,
        t.startAt,
        t.dueAt ?? '',
        t.completedAt ?? '',
      ]
        .map(escapeCsv)
        .join(','),
    ),
  ];

  // BOM لدعم العربية في Excel
  const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const datePart = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `taskflow-tasks-${companySlug}-${datePart}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
